import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/modal.css";

const AgreementModal = ({
  isOpen,
  onClose,
  title,
  content,
  agreementId,
  onAgree,
  userInfo,
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    // 모달이 열릴 때마다 스크롤 상태 초기화
    if (isOpen) {
      setHasScrolledToBottom(false);

      // 모달이 열릴 때 스크롤을 맨 위로 이동
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }

      // 스크롤 가능한지 확인
      setTimeout(() => {
        const element = contentRef.current;
        if (element) {
          if (element.scrollHeight <= element.clientHeight + 20) {
            // 스크롤이 필요 없는 경우 자동으로 스크롤 완료 처리
            setHasScrolledToBottom(true);
          }
        }
      }, 50);
    }
  }, [isOpen]);

  const handleScroll = (e) => {
    const element = e.target;
    // 스크롤이 바닥에 도달했는지 확인 (여유 20px 추가)
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 20) {
      setHasScrolledToBottom(true);
    }
  };

  // 내용이 짧아서 스크롤이 필요없는 경우 자동으로 버튼 활성화
  useEffect(() => {
    if (isOpen && contentRef.current) {
      const element = contentRef.current;
      if (element.scrollHeight <= element.clientHeight) {
        setHasScrolledToBottom(true);
      }
    }
  }, [isOpen, content]);

  const handleConfirm = () => {
    if (hasScrolledToBottom) {
      onAgree(agreementId);
    } else {
      // 스크롤을 자동으로 내려줌
      if (contentRef.current) {
        contentRef.current.scrollTo({
          top: contentRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  };

  // 스크롤을 아래로 자동으로 내리는 함수
  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // 애니메이션 최적화
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.25,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  const contentVariants = {
    hidden: {
      y: "100%",
    },
    visible: {
      y: 0,
      transition: {
        type: "spring",
        damping: 30,
        stiffness: 300,
        mass: 0.8,
      },
    },
    exit: {
      y: "100%",
      transition: {
        duration: 0.2,
        ease: [0.32, 0, 0.67, 0], // 부드러운 이징 함수 적용
      },
    },
  };

  // 아파트 이름, 동호수 정보 가져오기
  const apartmentName = userInfo?.apartmentName || "000";
  const apartmentDong = userInfo?.apartmentBlock || "000";
  const apartmentHo = userInfo?.apartmentUnit || "000";
  const residentName = userInfo?.residentName || "구분소유자";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="agreement-modal-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="agreement-modal-content"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="agreement-modal-header">
              <div className="close-button" onClick={onClose}>
                ×
              </div>
              <h1>{title}</h1>
            </div>

            <div
              className="agreement-modal-body"
              ref={contentRef}
              onScroll={handleScroll}
            >
              <div className="agreement-document">
                <h2 className="agreement-title">
                  채권(하자보수에 갈음하는 손해배상
                  <br />
                  청구권) 양도양수증서
                </h2>
                <p className="agreement-parties">
                  {apartmentName} 아파트 {apartmentDong}동 {apartmentHo}호
                  {residentName}
                  <br />
                  (이하, "갑" 이라 한다)와
                  <br />
                  {apartmentName} 입주자대표회의 (이하, "을" 이라 한다)는
                  <br />
                  다음과 같이 계약한다.
                </p>

                <h2 className="agreement-title">{title}</h2>

                <div className="agreement-text">{content}</div>
              </div>
            </div>

            {!hasScrolledToBottom && (
              <div className="scroll-indicator-bottom" onClick={scrollToBottom}>
                ↓ 내용을 끝까지 읽어주세요 ↓
              </div>
            )}

            <div
              className={`agreement-modal-footer ${
                !hasScrolledToBottom ? "no-border" : ""
              }`}
            >
              <button
                className="agreement-button"
                onClick={handleConfirm}
                disabled={!hasScrolledToBottom}
                style={{
                  backgroundColor: hasScrolledToBottom ? "#0075FF" : "#A9DAFF",
                  color: hasScrolledToBottom ? "white" : "#CCEAFF",
                  cursor: hasScrolledToBottom ? "pointer" : "not-allowed",
                }}
              >
                {hasScrolledToBottom ? "동의합니다" : "끝까지 읽어주세요"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AgreementModal;
